import { useCallback, useEffect, useMemo, useState } from "react";
import {
  supabase,
  supabasePublishableKey,
  supabaseUrl,
} from "../lib/supabase";
import { downloadCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";
import "./FiscalPage.css";

type FiscalConnection = {
  id: string;
  environment: string;
  connection_status: string;
  sire_rvie_enabled: boolean;
  sire_rce_enabled: boolean;
  automatic_sync: boolean;
  signing_enabled: boolean;
  cpe_transmission_enabled: boolean;
  sol_cpe_profile_confirmed: boolean;
  last_connection_test_at: string | null;
  last_successful_connection_at: string | null;
  last_sync_at: string | null;
  last_successful_sync_at: string | null;
  last_error_code: string | null;
  last_error_message: string | null;
};

type TaxPeriod = {
  id: string;
  year: number;
  month: number;
  period: string;
  status: string;
  rvie_status: string;
  rce_status: string;
  closed_at: string | null;
  updated_at: string;
};

type SirePeriod = {
  id: string;
  period: string;
  register_type: string;
  status: string;
  proposal_status: string | null;
  document_count: number;
  taxable_amount: number | string;
  tax_total: number | string;
  total: number | string;
  last_generated_at: string | null;
  last_submitted_at: string | null;
  last_completed_at: string | null;
  last_error_code: string | null;
  last_error_message: string | null;
};

type FiscalAlert = {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  status: string;
  detected_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
};

type FiscalCenter = {
  connection: FiscalConnection | null;
  tax_periods: TaxPeriod[];
  sire_periods: SirePeriod[];
  alerts: FiscalAlert[];
  queue: {
    pending: number | string;
    processing: number | string;
    retry: number | string;
    completed: number | string;
    failed: number | string;
  };
};

type RegisterSummary = {
  document_count: number | string;
  taxable_amount: number | string;
  tax_total: number | string;
  total: number | string;
};

type FiscalSection = "summary" | "sire" | "alerts";

type SireProcessHistory = {
  id: string;
  register_type: "RVIE" | "RCE";
  status: string;
  sunat_ticket: string | null;
  result_file_path: string | null;
  metadata: {
    period?: string;
    status_code?: string;
    status_message?: string;
    file_type?: string | number | null;
    process?: string | null;
    files?: Array<{ name?: string; type?: string | number | null; process?: string }>;
  } | null;
  created_at: string;
  completed_at: string | null;
};

type SireLiveResult = {
  ok?: boolean;
  action?: string;
  status?: string;
  code?: string;
  message?: string;
  period?: string;
  ticket?: string;
  register?: string;
  files?: Array<{ name?: string; type?: string | number | null; process?: string }>;
  count?: number;
  errors?: number;
};

function money(value: number | string | undefined) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function statusLabel(value: string | null | undefined) {
  const clean = String(value ?? "").toLowerCase();
  const labels: Record<string, string> = {
    configured: "Configurada",
    connected: "Conectada",
    active: "Activa",
    inactive: "Inactiva",
    pending: "Pendiente",
    processing: "Procesando",
    completed: "Completado",
    failed: "Con error",
    retry: "Reintento",
    open: "Abierto",
    closed: "Cerrado",
    generated: "Generado",
    submitted: "Presentado",
    ready: "Listo",
    error: "Error",
    production: "Producción",
    beta: "Pruebas",
    draft: "Borrador",
  };
  return labels[clean] ?? value ?? "—";
}

function periodLabel(period: string) {
  if (period.length !== 6) return period;
  return period.slice(4, 6) + "/" + period.slice(0, 4);
}

export function FiscalPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [center, setCenter] = useState<FiscalCenter | null>(null);
  const [period, setPeriod] = useState("");
  const [rvie, setRvie] = useState<RegisterSummary | null>(null);
  const [rce, setRce] = useState<RegisterSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sireLive, setSireLive] = useState<SireLiveResult | null>(null);
  const [sireHistory, setSireHistory] = useState<SireProcessHistory[]>([]);
  const [sireLiveLoading, setSireLiveLoading] = useState(false);
  const [rvieTicket, setRvieTicket] = useState("");
  const [rceTicket, setRceTicket] = useState("");
  const [downloadingSireFile, setDownloadingSireFile] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<FiscalSection>("summary");
  const [solProfileLoading, setSolProfileLoading] = useState(false);
  const [solReadiness, setSolReadiness] = useState<string | null>(null);

  const loadCenter = useCallback(async () => {
    if (!organizationId) {
      setCenter(null);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_fiscal_control_center",
      {
        p_organization_id: organizationId,
      },
    );

    if (rpcError) {
      setCenter(null);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    const next = data as FiscalCenter;
    setCenter(next);

    if (!period && next.tax_periods.length > 0) {
      setPeriod(next.tax_periods[0].period);
    }

    setLoading(false);
  }, [organizationId, period]);

  const loadSireHistory = useCallback(async () => {
    if (!organizationId) {
      setSireHistory([]);
      return;
    }

    const { data, error: historyError } = await supabase
      .from("sire_processes")
      .select(
        "id,register_type,status,sunat_ticket,result_file_path,metadata,created_at,completed_at",
      )
      .eq("organization_id", organizationId)
      .eq("process_type", "proposal_download")
      .order("created_at", { ascending: false })
      .limit(20);

    if (historyError) {
      setError(historyError.message);
      return;
    }

    const history = (data ?? []) as SireProcessHistory[];
    setSireHistory(history);

    const latestRvie = history.find(
      (item) => item.register_type === "RVIE" && item.sunat_ticket,
    );
    const latestRce = history.find(
      (item) => item.register_type === "RCE" && item.sunat_ticket,
    );

    if (latestRvie?.sunat_ticket) {
      setRvieTicket(latestRvie.sunat_ticket);
    }
    if (latestRce?.sunat_ticket) {
      setRceTicket(latestRce.sunat_ticket);
    }
  }, [organizationId]);

  const loadPeriod = useCallback(async () => {
    if (!organizationId || !period) {
      setRvie(null);
      setRce(null);
      return;
    }

    const [rvieResult, rceResult] = await Promise.all([
      supabase.rpc("get_accounting_rvie_summary", {
        p_organization_id: organizationId,
        p_period: period,
      }),
      supabase.rpc("get_accounting_rce_summary", {
        p_organization_id: organizationId,
        p_period: period,
      }),
    ]);

    const firstError = rvieResult.error ?? rceResult.error;
    if (firstError) setError(firstError.message);

    setRvie(
      rvieResult.error
        ? null
        : (((rvieResult.data ?? [])[0] as RegisterSummary | undefined) ?? null),
    );
    setRce(
      rceResult.error
        ? null
        : (((rceResult.data ?? [])[0] as RegisterSummary | undefined) ?? null),
    );
  }, [organizationId, period]);

  useEffect(() => {
    void loadCenter();
  }, [loadCenter]);

  useEffect(() => {
    void loadPeriod();
  }, [loadPeriod]);

  useEffect(() => {
    void loadSireHistory();
  }, [loadSireHistory]);

  const selectedPeriod = useMemo(
    () => center?.tax_periods.find((item) => item.period === period) ?? null,
    [center, period],
  );

  const openAlerts = useMemo(
    () => (center?.alerts ?? []).filter((alert) => alert.status === "open"),
    [center],
  );

  const invokeSire = useCallback(
    async (payload: Record<string, unknown>) => {
      const connectionId = center?.connection?.id;
      if (!connectionId) {
        setError("No existe una conexión SUNAT configurada para esta empresa.");
        return null;
      }

      setSireLiveLoading(true);
      setError(null);

      const { data, error: functionError } = await supabase.functions.invoke(
        "sunat-sire-readonly",
        {
          body: {
            connection_id: connectionId,
            ...payload,
          },
        },
      );

      setSireLiveLoading(false);

      if (functionError) {
        setError(functionError.message);
        return null;
      }

      const result = (data ?? null) as SireLiveResult | null;
      setSireLive(result);

      if (result?.ticket) {
        if (payload.action === "rce_start" || payload.action === "rce_status") {
          setRceTicket(result.ticket);
        } else if (payload.action === "rvie_start" || payload.action === "rvie_status") {
          setRvieTicket(result.ticket);
        }
      }

      if (
        result?.status === "requested" ||
        result?.status === "ready" ||
        result?.status === "pending"
      ) {
        void loadSireHistory();
      }

      return result;
    },
    [center?.connection?.id, loadSireHistory],
  );

  const testSireConnection = useCallback(async () => {
    await invokeSire({ action: "test" });
  }, [invokeSire]);

  const confirmSolCpeProfile = useCallback(async () => {
    const connectionId = center?.connection?.id;
    if (!connectionId) {
      setError("No existe una conexión SUNAT configurada.");
      return;
    }

    setSolProfileLoading(true);
    setError(null);

    const { error: confirmError } = await supabase.rpc(
      "confirm_sunat_sol_cpe_profile",
      {
        p_connection_id: connectionId,
        p_confirmed: true,
        p_note:
          "SUNAT confirmó el registro satisfactorio del usuario secundario con Servicio de Envío de Documentos Electrónicos por Servicio Web el 31/10/2026.",
      },
    );

    if (confirmError) {
      setSolProfileLoading(false);
      setError(confirmError.message);
      return;
    }

    const { data: readinessData, error: readinessError } = await supabase.rpc(
      "get_sunat_cpe_transmission_readiness",
      {
        p_connection_id: connectionId,
      },
    );

    if (readinessError) {
      setSolProfileLoading(false);
      setError(readinessError.message);
      return;
    }

    const readiness = Array.isArray(readinessData)
      ? readinessData[0]
      : readinessData;

    setSolReadiness(String(readiness?.readiness_state ?? "SIN ESTADO"));
    await loadCenter();
    setSolProfileLoading(false);
  }, [center?.connection?.id, loadCenter]);

  const requestRvieProposal = useCallback(async () => {
    if (!period) {
      setError("Selecciona un periodo antes de solicitar la propuesta RVIE.");
      return;
    }

    await invokeSire({
      action: "rvie_start",
      period,
      file_type: "xlsx",
    });
  }, [invokeSire, period]);

  const checkRvieTicket = useCallback(async () => {
    if (!period || !rvieTicket) {
      setError("No existe un ticket RVIE para consultar.");
      return;
    }

    await invokeSire({
      action: "rvie_status",
      period,
      ticket: rvieTicket,
    });
  }, [invokeSire, period, rvieTicket]);

  const requestRceProposal = useCallback(async () => {
    if (!period) {
      setError("Selecciona un periodo antes de solicitar la propuesta RCE.");
      return;
    }

    await invokeSire({
      action: "rce_start",
      period,
      file_type: "csv",
    });
  }, [invokeSire, period]);

  const checkRceTicket = useCallback(async () => {
    if (!period || !rceTicket) {
      setError("No existe un ticket RCE para consultar.");
      return;
    }

    await invokeSire({
      action: "rce_status",
      period,
      ticket: rceTicket,
    });
  }, [invokeSire, period, rceTicket]);

  const downloadSireFile = useCallback(
    async (
      file: { name?: string; type?: string | number | null; process?: string },
      context?: {
        ticket?: string | null;
        period?: string | null;
        register?: string | null;
      },
    ) => {
      const connectionId = center?.connection?.id;
      const fileName = String(file.name ?? "").trim();

      const ticket = String(context?.ticket ?? sireLive?.ticket ?? "").trim();
      const reportPeriod = String(
        context?.period ?? sireLive?.period ?? period,
      ).trim();
      const processCode = String(file.process ?? "").trim();
      const registerType = String(
        context?.register ?? sireLive?.register ?? "",
      ).trim();

      if (
        !connectionId ||
        !fileName ||
        !ticket ||
        !reportPeriod ||
        !processCode ||
        !registerType
      ) {
        setError("No existe información suficiente para descargar el archivo SIRE.");
        return;
      }

      setDownloadingSireFile(fileName);
      setError(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error("La sesión expiró. Inicia sesión nuevamente.");
        }

        const response = await fetch(
          supabaseUrl + "/functions/v1/sunat-sire-readonly",
          {
            method: "POST",
            headers: {
              Authorization: "Bearer " + session.access_token,
              apikey: supabasePublishableKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              connection_id: connectionId,
              action: "sire_download",
              file_name: fileName,
              file_type: file.type ?? null,
              period: reportPeriod,
              ticket,
              process: processCode,
              register: registerType,
            }),
          },
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(
            payload?.message ||
              payload?.code ||
              "No se pudo descargar el archivo SIRE.",
          );
        }

        const data = await response.blob();
        if (!data.size) {
          throw new Error("SUNAT devolvió un archivo vacío.");
        }

        const url = URL.createObjectURL(data);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (downloadError) {
        setError(
          downloadError instanceof Error
            ? downloadError.message
            : "No se pudo descargar el archivo SIRE.",
        );
      } finally {
        setDownloadingSireFile(null);
      }
    },
    [center?.connection?.id, period, sireLive?.period, sireLive?.ticket],
  );

  function exportRows(filename: string, rows: Record<string, unknown>[]) {
    if (rows.length === 0) {
      setError("No hay datos para exportar en el periodo seleccionado.");
      return;
    }

    const headers = Array.from(
      rows.reduce((set, row) => {
        Object.keys(row).forEach((key) => set.add(key));
        return set;
      }, new Set<string>()),
    );

    downloadCsv(filename, headers, rows);
  }

  async function exportRvieBase() {
    if (!organizationId || !period) {
      setError("Selecciona un periodo.");
      return;
    }

    const { data, error: rpcError } = await supabase.rpc(
      "get_accounting_sales_register",
      {
        p_organization_id: organizationId,
        p_period: period,
      },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    exportRows(
      `base-rvie-${period}.csv`,
      (data ?? []) as Record<string, unknown>[],
    );
  }

  async function exportRceBase() {
    if (!organizationId || !period) {
      setError("Selecciona un periodo.");
      return;
    }

    const { data, error: rpcError } = await supabase.rpc(
      "get_accounting_purchase_documents",
      {
        p_organization_id: organizationId,
        p_period: period,
      },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    exportRows(
      `base-rce-${period}.csv`,
      (data ?? []) as Record<string, unknown>[],
    );
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  const connection = center?.connection ?? null;
  const transmissionBlocked = !connection?.cpe_transmission_enabled;
  const sireNeverTested =
    Boolean(connection) &&
    !connection?.last_connection_test_at &&
    !connection?.last_successful_connection_at &&
    sireLive?.status !== "connected" &&
    sireLive?.status !== "requested" &&
    sireLive?.status !== "ready";
  const canRequestSire =
    Boolean(selectedOrganization.isPlatformAdmin) ||
    ["owner", "admin", "manager", "staff"].includes(selectedOrganization.role);

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Centro fiscal</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Supervisión interna de comprobantes, periodos y SIRE.</p>
        </div>

        <div className="quotation-actions">
          <button className="ghost-button" type="button" onClick={() => void exportRvieBase()} disabled={!period}>
            Exportar base RVIE
          </button>
          <button className="ghost-button" type="button" onClick={() => void exportRceBase()} disabled={!period}>
            Exportar base RCE
          </button>
          <button className="ghost-button" type="button" onClick={() => void loadCenter()}>
            Actualizar
          </button>
        </div>
      </header>

      <section
        className={
          transmissionBlocked
            ? "fiscal-safety-banner fiscal-safety-paused"
            : "fiscal-safety-banner fiscal-safety-enabled"
        }
      >
        <div>
          <strong>
            {transmissionBlocked
              ? "SIRE conectado · Envío CPE bloqueado"
              : "Transmisión CPE SUNAT habilitada"}
          </strong>
          <p>
            {sireNeverTested
              ? "La conexión está configurada, pero todavía no existe una prueba SIRE en vivo registrada. RVIE/RCE pueden probarse sin presentar libros ni transmitir CPE."
              : connection?.sol_cpe_profile_confirmed
                ? "SUNAT confirmó el perfil SOL para Servicio Web. La transmisión CPE continúa bloqueada hasta completar readiness y habilitarla de forma controlada."
                : "La conexión SIRE dispone de historial de prueba. Mientras SUNAT no confirme el perfil SOL para CPE, facturas, boletas y notas no se transmiten desde esta aplicación."}
          </p>
        </div>
        <span>{statusLabel(connection?.environment ?? "sin conexión")}</span>
      </section>

      {error ? <div className="inline-alert">{error}</div> : null}

      <nav className="fiscal-section-tabs" aria-label="Secciones del Centro Fiscal">
        <button
          type="button"
          className={activeSection === "summary" ? "fiscal-section-tab fiscal-section-tab-active" : "fiscal-section-tab"}
          onClick={() => setActiveSection("summary")}
        >
          Resumen
        </button>
        <button
          type="button"
          className={activeSection === "sire" ? "fiscal-section-tab fiscal-section-tab-active" : "fiscal-section-tab"}
          onClick={() => setActiveSection("sire")}
        >
          SIRE
        </button>
        <button
          type="button"
          className={activeSection === "alerts" ? "fiscal-section-tab fiscal-section-tab-active" : "fiscal-section-tab"}
          onClick={() => setActiveSection("alerts")}
        >
          Alertas ({openAlerts.length})
        </button>
      </nav>

      <section className={activeSection === "summary" ? "fiscal-summary-grid" : "fiscal-summary-grid fiscal-section-hidden"}>
        <div className="metric-card">
          <span>Conexión</span>
          <strong>{connection ? statusLabel(connection.connection_status) : "No configurada"}</strong>
        </div>
        <div className="metric-card">
          <span>Firma</span>
          <strong>{connection?.signing_enabled ? "Activa" : "Inactiva"}</strong>
        </div>
        <div className="metric-card">
          <span>RVIE</span>
          <strong>{connection?.sire_rvie_enabled ? "Habilitado" : "Inactivo"}</strong>
        </div>
        <div className="metric-card">
          <span>RCE</span>
          <strong>{connection?.sire_rce_enabled ? "Habilitado" : "Inactivo"}</strong>
        </div>
        <div className="metric-card">
          <span>Prueba SIRE</span>
          <strong>
            {connection?.last_successful_connection_at
              ? "Validada"
              : connection?.last_connection_test_at
                ? "Probada con incidencia"
                : sireLive?.status === "connected" ||
                    sireLive?.status === "requested" ||
                    sireLive?.status === "ready"
                  ? "Validada en esta sesión"
                  : "No registrada"}
          </strong>
        </div>        <div className="metric-card">
          <span>Perfil SOL CPE</span>
          <strong>
            {connection?.sol_cpe_profile_confirmed
              ? "Confirmado"
              : "No confirmado"}
          </strong>
          {selectedOrganization.isPlatformAdmin &&
          connection &&
          !connection.sol_cpe_profile_confirmed ? (
            <button
              type="button"
              className="ghost-button fiscal-profile-confirm-button"
              disabled={solProfileLoading}
              onClick={() => void confirmSolCpeProfile()}
            >
              {solProfileLoading
                ? "Confirmando…"
                : "Confirmar perfil SOL"}
            </button>
          ) : null}
          {solReadiness ? (
            <span className="fiscal-readiness">
              Readiness: {solReadiness}
            </span>
          ) : null}
        </div>
        <div className="metric-card">
          <span>Cola pendiente</span>
          <strong>{Number(center?.queue.pending ?? 0)}</strong>
        </div>
        <div className="metric-card">
          <span>Cola con error</span>
          <strong>
            {Number(center?.queue.failed ?? 0) + Number(center?.queue.retry ?? 0)}
          </strong>
        </div>
        <div className="metric-card">
          <span>Alertas abiertas</span>
          <strong>{openAlerts.length}</strong>
        </div>
      </section>

      <section className={activeSection === "sire" ? "panel-card" : "panel-card fiscal-section-hidden"}>
        <div className="section-heading fiscal-period-heading">
          <div>
            <span className="eyebrow">Periodo tributario</span>
            <h2>RVIE y RCE</h2>
          </div>

          <select
            className="fiscal-period-select"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          >
            <option value="">Seleccionar periodo</option>
            {(center?.tax_periods ?? []).map((item) => (
              <option key={item.id} value={item.period}>
                {periodLabel(item.period)} · {statusLabel(item.status)}
              </option>
            ))}
          </select>
        </div>

        {selectedPeriod ? (
          <div className="fiscal-period-status">
            <span>RVIE: {statusLabel(selectedPeriod.rvie_status)}</span>
            <span>RCE: {statusLabel(selectedPeriod.rce_status)}</span>
            <span>Periodo: {statusLabel(selectedPeriod.status)}</span>
          </div>
        ) : null}

        <div className="fiscal-register-grid">
          <article>
            <span className="eyebrow">RVIE · Ventas</span>
            <strong>{Number(rvie?.document_count ?? 0)} documentos</strong>
            <dl>
              <div>
                <dt>Base</dt>
                <dd>{money(rvie?.taxable_amount)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(rvie?.tax_total)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{money(rvie?.total)}</dd>
              </div>
            </dl>
          </article>

          <article>
            <span className="eyebrow">RCE · Compras</span>
            <strong>{Number(rce?.document_count ?? 0)} documentos</strong>
            <dl>
              <div>
                <dt>Base</dt>
                <dd>{money(rce?.taxable_amount)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(rce?.tax_total)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{money(rce?.total)}</dd>
              </div>
            </dl>
          </article>
        </div>
      </section>

      <section className={activeSection === "sire" ? "panel-card" : "panel-card fiscal-section-hidden"}>
        <div className="section-heading">
          <div>
            <span className="eyebrow">SUNAT · SIRE en vivo</span>
            <h2>RVIE y RCE de solo lectura</h2>
          </div>
        </div>

        <p className="settings-empty">
          Estas acciones autentican con SUNAT y solicitan las propuestas RVIE y RCE.
          No presentan libros, no envían CPE y no habilitan transmisión.
          {sireNeverTested
            ? " Aún no existe una prueba SIRE en vivo registrada para esta conexión."
            : ""}
        </p>

        <div className="fiscal-live-actions">
          <button
            type="button"
            className="ghost-button"
            disabled={sireLiveLoading || !connection}
            onClick={() => void testSireConnection()}
          >
            {sireLiveLoading ? "Procesando…" : "Probar conexión SUNAT"}
          </button>

          <button
            type="button"
            disabled={sireLiveLoading || !connection || !period || !canRequestSire}
            onClick={() => void requestRvieProposal()}
          >
            Solicitar propuesta RVIE
          </button>

          <button
            type="button"
            className="ghost-button"
            disabled={sireLiveLoading || !rvieTicket}
            onClick={() => void checkRvieTicket()}
          >
            Consultar ticket RVIE
          </button>

          <button
            type="button"
            disabled={sireLiveLoading || !connection || !period || !canRequestSire}
            onClick={() => void requestRceProposal()}
          >
            Solicitar propuesta RCE
          </button>

          <button
            type="button"
            className="ghost-button"
            disabled={sireLiveLoading || !rceTicket}
            onClick={() => void checkRceTicket()}
          >
            Consultar ticket RCE
          </button>
        </div>

        {sireLive ? (
          <div className="fiscal-live-result">
            <strong>
              {sireLive.ok === false
                ? "SUNAT respondió con una incidencia"
                : sireLive.status === "ready"
                  ? (sireLive.action === "rce_status" ? "RCE listo" : "RVIE listo")
                  : sireLive.status === "requested"
                    ? (sireLive.action === "rce_start" ? "Solicitud RCE registrada" : "Solicitud RVIE registrada")
                    : sireLive.status === "connected"
                      ? "Conexión SUNAT correcta"
                      : "Estado SUNAT actualizado"}
            </strong>

            {sireLive.ticket ? <span>Ticket: {sireLive.ticket}</span> : null}
            {sireLive.message ? <span>{sireLive.message}</span> : null}
            {sireLive.code ? <span>Código: {sireLive.code}</span> : null}

            {(sireLive.files ?? []).map((file, index) => (
              <div
                className="fiscal-live-file"
                key={(file.name ?? "archivo") + index}
              >
                <span>Archivo: {file.name ?? "sin nombre"}</span>
                {file.name ? (
                  <button
                    type="button"
                    className="ghost-button"
                    disabled={downloadingSireFile === file.name}
                    onClick={() =>
                      void downloadSireFile(file, {
                        register: sireLive.register ?? null,
                      })
                    }
                  >
                    {downloadingSireFile === file.name
                      ? "Descargando…"
                      : "Descargar ZIP"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <p className="settings-empty">
          {canRequestSire
            ? "RVIE y RCE operan en modo de consulta: generan tickets de exportación, sin presentar libros ni transmitir CPE."
            : "Tu rol es de solo lectura: puedes consultar estados, pero no generar nuevos tickets SIRE."}
        </p>
      </section>


      <section className={activeSection === "sire" ? "panel-card" : "panel-card fiscal-section-hidden"}>
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historial SIRE</span>
            <h2>Tickets y archivos generados</h2>
          </div>
        </div>

        {sireHistory.length === 0 ? (
          <p className="settings-empty">
            Todavía no hay tickets SIRE persistidos para esta empresa.
          </p>
        ) : (
          <div className="fiscal-sire-history">
            {sireHistory.map((item) => {
              const storedFile =
                item.metadata?.files?.[0] ??
                (item.result_file_path
                  ? {
                      name: item.result_file_path,
                      type: item.metadata?.file_type ?? null,
                      process: item.metadata?.process ?? undefined,
                    }
                  : null);

              return (
                <article key={item.id}>
                  <div>
                    <strong>
                      {item.register_type} · {item.sunat_ticket ?? "sin ticket"}
                    </strong>
                    <span>
                      {item.metadata?.period
                        ? periodLabel(item.metadata.period)
                        : "Periodo no registrado"} · {statusLabel(item.status)}
                    </span>
                    {storedFile?.name ? (
                      <span>Archivo: {storedFile.name}</span>
                    ) : null}
                  </div>

                  <div className="fiscal-sire-history-actions">
                    {item.sunat_ticket && item.metadata?.period ? (
                      <button
                        type="button"
                        className="ghost-button"
                        disabled={sireLiveLoading}
                        onClick={() =>
                          void invokeSire({
                            action:
                              item.register_type === "RCE"
                                ? "rce_status"
                                : "rvie_status",
                            period: item.metadata?.period,
                            ticket: item.sunat_ticket,
                          })
                        }
                      >
                        Consultar estado
                      </button>
                    ) : null}

                    {storedFile?.name &&
                    item.sunat_ticket &&
                    item.metadata?.period &&
                    storedFile.process ? (
                      <button
                        type="button"
                        className="ghost-button"
                        disabled={downloadingSireFile === storedFile.name}
                        onClick={() =>
                          void downloadSireFile(storedFile, {
                            ticket: item.sunat_ticket,
                            period: item.metadata?.period ?? null,
                            register: item.register_type,
                          })
                        }
                      >
                        {downloadingSireFile === storedFile.name
                          ? "Descargando…"
                          : "Descargar ZIP"}
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className={activeSection === "alerts" ? "panel-card" : "panel-card fiscal-section-hidden"}>
        <div className="section-heading">
          <div>
            <span className="eyebrow">Alertas</span>
            <h2>Control fiscal interno</h2>
          </div>
        </div>

        <div className="fiscal-alert-list">
          {(center?.alerts ?? []).map((alert) => (
            <article key={alert.id}>
              <div>
                <span className={"status-badge fiscal-severity-" + alert.severity}>
                  {alert.severity}
                </span>
                <strong>{alert.title}</strong>
                <p>{alert.message}</p>
              </div>
              <div className="fiscal-alert-meta">
                <span>{statusLabel(alert.status)}</span>
                <time>
                  {new Date(alert.detected_at).toLocaleString("es-PE")}
                </time>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={activeSection === "sire" ? "panel-card" : "panel-card fiscal-section-hidden"}>
        <div className="section-heading">
          <div>
            <span className="eyebrow">SIRE interno</span>
            <h2>Periodos procesados</h2>
          </div>
        </div>

        {(center?.sire_periods ?? []).length === 0 ? (
          <p className="settings-empty">
            No hay periodos SIRE internos procesados en esta empresa.
          </p>
        ) : (
          <div className="fiscal-sire-list">
            {(center?.sire_periods ?? []).map((item) => (
              <article key={item.id}>
                <div>
                  <strong>
                    {periodLabel(item.period)} · {item.register_type}
                  </strong>
                  <span>{statusLabel(item.status)}</span>
                </div>
                <div>
                  <span>{item.document_count} docs.</span>
                  <strong>{money(item.total)}</strong>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {loading ? (
        <section className="empty-state">
          <strong>Cargando control fiscal…</strong>
        </section>
      ) : null}
    </div>
  );
}